/**
 * Mobile Form Enhancements for Sleepy Sloth Theme
 * Adds clear buttons and password toggles to form inputs
 */

(function() {
  'use strict';

  // SVG icons as strings
  const ICONS = {
    clear: '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>',
    showPassword: '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>',
    hidePassword: '<svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd"/><path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"/></svg>'
  };

  /**
   * Wrap an input in a wrapper div for positioning buttons
   */
  function wrapInput(input) {
    if (input.parentElement.classList.contains('ss-input-wrapper')) {
      return input.parentElement;
    }
    
    const wrapper = document.createElement('div');
    wrapper.className = 'ss-input-wrapper';
    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(input);
    return wrapper;
  }

  /**
   * Add clear button to text, email, tel, search, url inputs
   */
  function addClearButton(input) {
    // Don't add clear button to password fields
    if (input.type === 'password') {
      return;
    }

    const wrapper = wrapInput(input);
    
    // Check if clear button already exists after wrapping
    if (wrapper.querySelector('.ss-input-clear')) {
      return;
    }
    
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'ss-input-clear';
    clearBtn.setAttribute('aria-label', 'Clear input');
    clearBtn.innerHTML = ICONS.clear;
    
    // Show/hide clear button based on input value
    const toggleClearButton = () => {
      if (input.value.length > 0) {
        clearBtn.classList.add('visible');
      } else {
        clearBtn.classList.remove('visible');
      }
    };
    
    // Clear input when button is clicked
    clearBtn.addEventListener('click', () => {
      input.value = '';
      input.focus();
      toggleClearButton();
      // Trigger input event for any listeners
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    
    // Update clear button visibility on input
    input.addEventListener('input', toggleClearButton);
    input.addEventListener('change', toggleClearButton);
    
    // Initial state
    toggleClearButton();
    
    wrapper.appendChild(clearBtn);
  }

  /**
   * Add password visibility toggle to password inputs
   */
  function addPasswordToggle(input) {
    const wrapper = wrapInput(input);
    
    // Check if toggle already exists after wrapping
    if (wrapper.querySelector('.ss-password-toggle')) {
      return;
    }
    
    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.className = 'ss-password-toggle';
    toggleBtn.setAttribute('aria-label', 'Toggle password visibility');
    toggleBtn.innerHTML = ICONS.showPassword;
    
    let isPasswordVisible = false;
    
    toggleBtn.addEventListener('click', () => {
      isPasswordVisible = !isPasswordVisible;
      
      if (isPasswordVisible) {
        input.type = 'text';
        toggleBtn.innerHTML = ICONS.hidePassword;
        toggleBtn.setAttribute('aria-label', 'Hide password');
      } else {
        input.type = 'password';
        toggleBtn.innerHTML = ICONS.showPassword;
        toggleBtn.setAttribute('aria-label', 'Show password');
      }
      
      // Keep focus on input
      input.focus();
    });
    
    wrapper.appendChild(toggleBtn);
  }

  /**
   * Initialize form enhancements
   */
  function initFormEnhancements() {
    // Select all relevant inputs
    const textInputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="search"], input[type="url"]');
    const passwordInputs = document.querySelectorAll('input[type="password"]');
    
    // Add clear buttons to text-based inputs
    textInputs.forEach(input => {
      // Skip hidden inputs and inputs with data attribute to exclude
      if (input.offsetParent !== null && !input.hasAttribute('data-no-clear')) {
        addClearButton(input);
      }
    });
    
    // Add password toggles
    passwordInputs.forEach(input => {
      // Skip hidden inputs and inputs with data attribute to exclude
      if (input.offsetParent !== null && !input.hasAttribute('data-no-toggle')) {
        addPasswordToggle(input);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFormEnhancements);
  } else {
    initFormEnhancements();
  }

  // Re-initialize for dynamically added forms (e.g., AJAX loaded content)
  // Throttle observer to improve performance
  let observerTimeout;
  const observer = new MutationObserver((mutations) => {
    // Clear existing timeout
    if (observerTimeout) {
      clearTimeout(observerTimeout);
    }
    
    // Debounce mutations to avoid excessive processing
    observerTimeout = setTimeout(() => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is an input or contains inputs
            if (node.tagName === 'INPUT') {
              const type = node.type;
              if (['text', 'email', 'tel', 'search', 'url'].includes(type)) {
                addClearButton(node);
              } else if (type === 'password') {
                addPasswordToggle(node);
              }
            } else if (node.querySelectorAll) {
              // Check for inputs within the added node
              const textInputs = node.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], input[type="search"], input[type="url"]');
              const passwordInputs = node.querySelectorAll('input[type="password"]');
              
              textInputs.forEach(input => {
                if (input.offsetParent !== null && !input.hasAttribute('data-no-clear')) {
                  addClearButton(input);
                }
              });
              
              passwordInputs.forEach(input => {
                if (input.offsetParent !== null && !input.hasAttribute('data-no-toggle')) {
                  addPasswordToggle(input);
                }
              });
            }
          }
        });
      });
    }, 100); // 100ms debounce
  });

  // Start observing the document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
